import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, MousePointerClick, Eye, Filter, Plus, ArrowUpRight } from 'lucide-react';

export const AdsDashboardMockup: React.FC = () => {
  const [animatedROAS, setAnimatedROAS] = useState(0);

  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      if (current >= 8.5) {
        clearInterval(interval);
      } else {
        current += 0.5;
        setAnimatedROAS(current);
      }
    }, 40);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full bg-[#1E293B] text-white py-16 px-4 md:px-12 font-sans border-b border-slate-700">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
         
         {/* LEFT: TEXT CONTENT */}
         <div className="lg:col-span-4 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-3 py-1.5 rounded-full text-xs font-bold border border-blue-500/30">
               <TrendingUp className="w-3.5 h-3.5" />
               Performance & Growth Ads
            </div>
            
            <h2 className="text-3xl md:text-5xl font-heading font-black tracking-tight leading-tight text-white">
               Você já conhece tráfego pago?
            </h2>

            <p className="text-slate-400 text-sm md:text-base font-light leading-relaxed">
               Colocamos sua marca na principal avenida digital da sua cidade. Campanhas segmentadas no Google Ads, Facebook e Instagram.
            </p>

            <div className="bg-slate-800/80 border border-slate-700 p-5 rounded-2xl space-y-2 text-left">
               <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Média de ROAS Alcançada</span>
                  <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                     +24% este mês
                  </span>
               </div>
               <div className="flex items-baseline gap-2">
                  <strong className="text-4xl font-black text-emerald-400">{animatedROAS.toFixed(1)}x</strong>
                  <span className="text-xs text-slate-400 font-medium">retorno sobre investimento</span>
               </div>
            </div>
         </div>

         {/* RIGHT: DASHBOARD DISPLAY */}
         <div className="lg:col-span-8 flex justify-center">
            <div className="w-full max-w-[760px] bg-[#0F172A] border border-slate-700 rounded-2xl overflow-hidden shadow-sm text-left">
               
               {/* Browser Header */}
               <div className="bg-[#1E293B] px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                        <div className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                        <div className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                     </div>
                     <span className="text-xs font-bold text-slate-300 hidden sm:inline">Facebook Ads Report • Meta Business</span>
                  </div>
                  <div className="flex gap-2">
                     <button className="bg-slate-800 text-slate-300 text-[10px] font-bold px-2.5 py-1 rounded border border-slate-600 flex items-center gap-1">
                        <Filter className="w-3 h-3" /> Filtros
                     </button>
                     <button className="bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Criar Anúncio
                     </button>
                  </div>
               </div>

               {/* Dashboard Metrics Grid */}
               <div className="p-4 md:p-6 space-y-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                     {[
                        { label: 'Investimento', value: 'R$ 4.520,00', change: '+12.4%', icon: DollarSign, color: 'text-blue-400' },
                        { label: 'Cliques no Link', value: '18.492', change: '+18.2%', icon: MousePointerClick, color: 'text-purple-400' },
                        { label: 'CPC Médio', value: 'R$ 0,24', change: '-8.5%', icon: TrendingUp, color: 'text-emerald-400' },
                        { label: 'Impressões', value: '342.100', change: '+32.1%', icon: Eye, color: 'text-amber-400' }
                     ].map((item, idx) => (
                        <div key={idx} className="bg-[#1E293B] border border-slate-700/80 p-3 rounded-xl space-y-1">
                           <div className="flex justify-between items-center">
                              <span className="text-[10px] font-bold text-slate-400">{item.label}</span>
                              <item.icon className={`w-3.5 h-3.5 ${item.color}`} />
                           </div>
                           <strong className="text-base font-black text-white block">{item.value}</strong>
                           <span className="text-[9px] font-bold text-emerald-400 block">{item.change}</span>
                        </div>
                     ))}
                  </div>

                  {/* SVG Chart */}
                  <div className="bg-[#1E293B] border border-slate-700/80 p-4 rounded-xl space-y-3">
                     <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-300">Desempenho de Conversões em Tempo Real</span>
                        <span className="text-[10px] text-slate-400 bg-slate-800 px-2 py-0.5 rounded border border-slate-700">30 dias</span>
                     </div>
                     <div className="h-32 w-full relative pt-2">
                        <svg className="w-full h-full" viewBox="0 0 500 100" preserveAspectRatio="none">
                           <path 
                              d="M0,80 Q100,40 200,60 T400,20 T500,10" 
                              fill="none" 
                              stroke="#3B82F6" 
                              strokeWidth="3" 
                              strokeLinecap="round" 
                           />
                        </svg>
                     </div>
                  </div>
               </div>

            </div>
         </div>

      </div>
    </div>
  );
};
